import { useState } from 'react';

import CreateIcon from '@material-ui/icons/Create';
import ErrorOutlineRoundedIcon from '@material-ui/icons/ErrorOutlineRounded';
import {
  BasicDialog,
  Button,
  CoverageArea,
  LinearProgress,
  MapboxMap,
  Market,
  MessageBox,
} from '@opendoor/mosaic';

import { RegionFields } from '../../graphql/athena/types/RegionFields';
import { useUpdateVendorBranch } from '../../graphql/mutations/bender/useUpdateVendorBranch';
import { useCreateRegion } from '../../graphql/mutations/fence/useCreateRegion';
import { useUpdateRegion } from '../../graphql/mutations/fence/useUpdateRegion';
import useGetMarkets from '../../graphql/queries/fence/useGetMarkets';
import useGetRegion from '../../graphql/queries/fence/useGetRegion';
import ServiceAreaIcon from '../../icons/ServiceAreaIcon';
import { MapboxApiKey, OPENDOOR_HQ } from '../../utils/constants';
import { notEmpty } from '../../utils/types';
import HorizontalLayout from '../layouts/HorizontalLayout';
import VerticalLayout from '../layouts/VerticalLayout';

const regionToMarket = (region: RegionFields): Market => {
  return {
    identifier: region.identifier,
    details: {
      postalCodes: region.details?.postalCodes || [],
    },
  };
};

const regionToCoverageArea = (region?: RegionFields | null): CoverageArea | undefined => {
  if (!region) return undefined;
  return {
    postalCodes: region.details?.postalCodes || [],
  };
};

interface CoverageMapProps {
  regionId?: string;
  vendorBranch?: any;
}

const CoverageMap = ({ regionId, vendorBranch }: CoverageMapProps) => {
  const {
    data: regionData,
    loading: regionLoading,
    error: regionError,
    refetch: regionRefetch,
  } = useGetRegion({
    variables: {
      id: regionId,
    },
    skip: !regionId,
  });

  const {
    data: marketsData,
    loading: marketsLoading,
    error: marketsError,
    refetch: marketsRefetch,
  } = useGetMarkets();

  const [updateRegion] = useUpdateRegion();
  const [createRegion] = useCreateRegion();
  const [updateVendorBranch] = useUpdateVendorBranch();

  const [openEditModal, setOpenEditModal] = useState(false);

  const region = regionData?.fence?.getRegion?.region ?? vendorBranch?.fence;
  const coverageArea = regionToCoverageArea(region);
  const defaultMarket = region ? regionToMarket(region) : undefined;
  const markets =
    marketsData?.fence?.getRegionsFromTags?.regions?.filter(notEmpty).map(regionToMarket) || [];

  const apiKey = MapboxApiKey;
  if (!apiKey) {
    console.error('The Mapbox Api Key is missing');
    return null;
  }

  let error = undefined;
  let refetch = undefined;
  if (regionError && marketsError) {
    error = `Error fetching the service area and Opendoor markets: ${regionError}\n${marketsError}`;
    refetch = () => {
      regionRefetch();
      marketsRefetch();
    };
  } else if (regionError) {
    error = `Error fetching the service area: ${regionError}`;
    refetch = () => {
      regionRefetch();
    };
  } else if (marketsError) {
    error = `Error fetching the Opendoor markets: ${marketsError}`;
    refetch = () => {
      marketsRefetch();
    };
  }

  // TODO: If a new region is a market (vs custom) we don't update the region, just set it on the vendor branch.
  const onSubmit = async (coverage: CoverageArea) => {
    const isMarketRegion = regionData?.fence?.getRegion?.region?.tagIdentifiers?.find(
      (identifier) => identifier === 'market',
    );

    if (isMarketRegion) {
      const vendor = vendorBranch?.vendor;
      const organization = vendorBranch?.vendor?.organization;
      const createRegionResponse = await createRegion({
        variables: {
          identifier: `${vendor.name}-${vendorBranch.name}-${Math.random()}`,
          postalCodes: coverageArea?.postalCodes,
          organizationUuid: [organization.uuid, OPENDOOR_HQ],
        },
      });

      if (createRegionResponse.errors) {
        error = 'Error creating a new region';
        regionRefetch();
      } else {
        const vendorBranchId = vendorBranch?.id;
        const fenceIdentifier = createRegionResponse?.data?.fence?.createRegion?.region?.identifier;

        await updateVendorBranch({
          variables: {
            id: vendorBranchId,
            // @ts-expect-error Fix Object literal may only specify known properties, and 'fenceId' does not exist in type 'UpdateVendorBranchVariables'.
            fenceId: fenceIdentifier,
          },
        });
      }
    } else {
      await updateRegion({
        variables: {
          identifier: regionId,
          postalCodes: coverage.postalCodes,
        },
      });
    }
  };

  return (
    <VerticalLayout className="block h-11 pa-5 bg-white ma-4 bo-radius-2">
      <HorizontalLayout className="justify-between items-center">
        <HorizontalLayout className="items-center">
          <ServiceAreaIcon />
          <h2 className="pl-4">Service Area Coverage</h2>
        </HorizontalLayout>
        <div
          className="items-center blue6 underline-hover cursor-pointer"
          onClick={() => setOpenEditModal(true)}
        >
          <CreateIcon className="pr-1" style={{ width: 14, height: 14 }} />
          Edit
        </div>
        {region && defaultMarket && (
          <BasicDialog
            title="Edit Service Area"
            content={
              <MapboxMap
                apiKey={apiKey}
                markets={markets}
                onSubmit={onSubmit}
                editable
                currentCoverage={coverageArea}
                defaultMarket={defaultMarket}
              />
            }
            open={openEditModal}
            showCloseIcon
            onCancel={() => setOpenEditModal(false)}
            dialogProps={{
              maxWidth: 'xl',
              fullWidth: true,
            }}
          />
        )}
      </HorizontalLayout>
      {(regionLoading || marketsLoading) && <LinearProgress variant="query" />}
      {error && (
        <MessageBox
          image={<ErrorOutlineRoundedIcon className="h-5 w-5 mh-auto" />}
          description={error}
          extra={
            <Button className="mt-4" color="primary" onClick={refetch}>
              Please try again
            </Button>
          }
        />
      )}
      {region && defaultMarket && (
        <MapboxMap
          apiKey={apiKey}
          markets={markets}
          editable={false}
          onSubmit={onSubmit}
          currentCoverage={coverageArea}
          defaultMarket={defaultMarket}
        />
      )}
    </VerticalLayout>
  );
};

export default CoverageMap;
